import { useState, createContext, useContext, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import useIsMobile from '@/hooks/useIsMobile'
import { theme } from '@/lib/theme'

interface HelpContextType {
  helpEnabled: boolean
  toggleHelp: () => void
}

const HelpContext = createContext<HelpContextType>({
  helpEnabled: false,
  toggleHelp: () => {},
})

export const useHelp = () => useContext(HelpContext)

interface HelpProviderProps {
  children: React.ReactNode
}

export function FileExplorerHelpProvider({ children }: HelpProviderProps) {
  const [helpEnabled, setHelpEnabled] = useState(false)

  const toggleHelp = () => {
    setHelpEnabled((prev) => !prev)
  }

  return (
    <HelpContext.Provider value={{ helpEnabled, toggleHelp }}>
      {children}
    </HelpContext.Provider>
  )
}

interface HelpToggleProps {
  style?: React.CSSProperties
}

export function HelpToggle({ style }: HelpToggleProps) {
  const { t } = useTranslation()
  const { helpEnabled, toggleHelp } = useHelp()
  const { colors } = useThemeMode()
  const isMobile = useIsMobile()
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 })
  const iconRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (showTooltip && iconRef.current && !isMobile) {
      const rect = iconRef.current.getBoundingClientRect()
      const top = rect.top - 8
      const left = rect.left + rect.width / 2
      setTooltipPos({ top, left })
    }
  }, [showTooltip, isMobile])

  return (
    <>
      <span
        ref={iconRef}
        onClick={toggleHelp}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          ...style,
        }}
        onMouseEnter={() => !isMobile && setShowTooltip(true)}
        onMouseLeave={() => !isMobile && setShowTooltip(false)}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: helpEnabled ? colors.primaryAccent : colors.primaryBg,
            color: helpEnabled ? colors.primaryBg : colors.primaryAccent,
            border: `2px solid ${colors.primaryAccent}`,
            fontSize: '16px',
            fontWeight: 'bold',
            fontFamily: 'monospace',
            transition: 'all 0.2s ease',
          }}
        >
          ?
        </span>
      </span>
      {showTooltip && !isMobile && createPortal(
        <span
          style={{
            position: 'fixed',
            zIndex: 100001,
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left}px`,
            transform: 'translate(-50%, -100%)',
            padding: '8px 12px',
            backgroundColor: colors.primaryBg,
            color: colors.primaryText,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '6px',
            fontSize: theme.fontSize.xs,
            fontFamily: '"Departure Mono", monospace',
            maxWidth: '280px',
            whiteSpace: 'nowrap',
            lineHeight: '1.4',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            pointerEvents: 'none',
          }}
        >
          {t('help.fileExplorer.tooltip')}
        </span>,
        document.body
      )}
    </>
  )
}

interface HelpIconProps {
  text: string
  position?: 'top' | 'bottom' | 'left' | 'right'
}

export function HelpIcon({ text, position = 'top' }: HelpIconProps) {
  const { helpEnabled } = useHelp()
  const { colors } = useThemeMode()
  const isMobile = useIsMobile()
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 })
  const iconRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (showTooltip && iconRef.current && !isMobile) {
      const rect = iconRef.current.getBoundingClientRect()

      let top = 0
      let left = 0

      switch (position) {
        case 'bottom':
          top = rect.bottom + 8
          left = rect.left + rect.width / 2
          break
        case 'left':
          top = rect.top + rect.height / 2
          left = rect.left - 8
          break
        case 'right':
          top = rect.top + rect.height / 2
          left = rect.right + 8
          break
        case 'top':
        default:
          top = rect.top - 8
          left = rect.left + rect.width / 2
          break
      }

      setTooltipPos({ top, left })
    }
  }, [showTooltip, position, isMobile])

  // Hide completely on mobile since tooltips don't work well
  if (!helpEnabled || isMobile) {
    return null
  }

  const getTooltipTransform = () => {
    switch (position) {
      case 'bottom':
        return 'translateX(-50%)'
      case 'left':
        return 'translate(-100%, -50%)'
      case 'right':
        return 'translateY(-50%)'
      case 'top':
      default:
        return 'translate(-50%, -100%)'
    }
  }

  return (
    <>
      <span
        ref={iconRef}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: '6px',
          cursor: 'help',
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: colors.primaryAccent,
            color: colors.primaryBg,
            fontSize: '11px',
            fontWeight: 'bold',
            fontFamily: 'monospace',
          }}
        >
          ?
        </span>
      </span>
      {showTooltip && createPortal(
        <span
          style={{
            position: 'fixed',
            zIndex: 100001,
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left}px`,
            transform: getTooltipTransform(),
            padding: '8px 12px',
            backgroundColor: colors.primaryBg,
            color: colors.primaryText,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '6px',
            fontSize: theme.fontSize.xs,
            fontFamily: '"Departure Mono", monospace',
            maxWidth: '280px',
            whiteSpace: 'normal',
            lineHeight: '1.4',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </>
  )
}
